import { Images } from '@/assets/images';
import Button from '@/components/common/button';
import { useBlobFile } from '@/hooks';
import { isMobile } from '@/utils/mobile.util';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AVATAR_ACCEPT, REGEX } from 'constant';
import {
	cls,
	convertAppleFileToJpgFile,
	onDownloadFileFromServer,
	onDownloadFileLocal,
	resizeImage,
	validateAllowFile,
} from 'utils';

import { isNotNilOrEmpty, isString } from 'ramda-adjunct';

const Avatar: React.FC<{
	containerClassName?: string;
	isSystemUser?: boolean;
	hiddenButtonDownload?: boolean;
	hiddenButtonRemove?: boolean;
	avatarFile: string;
	onChange?: (avatarFile: File) => void;
	onRemoveImage?: () => void;
	fileName?: string;
}> = ({
	containerClassName,
	isSystemUser,
	hiddenButtonDownload,
	onChange,
	onRemoveImage,
	avatarFile,
	fileName,
}) => {
	const { t } = useTranslation(['common', 'validation']);

	const [value, setValue] = useState<File | string>('');

	const [isLoading, setIsLoading] = useState(false);

	const inputUploadAvatar = useRef<HTMLInputElement>(null);

	const onUploadAvatar = () => {
		if (!isSystemUser) {
			const inputRefCurrent =
				inputUploadAvatar && inputUploadAvatar.current
					? inputUploadAvatar.current
					: null;
			inputRefCurrent && inputRefCurrent.click();
		}
	};

	const { filePath } = useBlobFile(value, 'blob');

	const onDownloadAvatar = useCallback(() => {
		if (isString(value)) {
			onDownloadFileFromServer(value, fileName);
		} else {
			onDownloadFileLocal(value as File);
		}
	}, [onDownloadFileFromServer, onDownloadFileLocal, value, fileName]);

	const onRemoveAvatar = useCallback(() => {
		onRemoveImage?.();
		setValue('');
	}, [onRemoveImage, setValue]);

	const handleChangeAvatar = async (e: React.ChangeEvent<HTMLInputElement>) => {
		setIsLoading(true);
		if (e.target.files != null) {
			let image = e.target.files[0];
			// if (image?.size > FILE_SIZE_2MB_BYTE) {
			// 	showAlert(ALERT_39(FILE_SIZE_2MB), NOOP);
			// 	return;
			// }

			if (isNotNilOrEmpty(image)) {
				if (
					!validateAllowFile(
						image,
						REGEX.ALLOW_IMAGES,
						t('validation:ALERT_6'),
						true,
						t('validation:ALERT_6')
					)
				)
					return;
			}

			try {
				if (image.name?.toLocaleLowerCase().match(REGEX.APPLE_FILES)) {
					image = await convertAppleFileToJpgFile(image);
				}

				const newImageResized = await resizeImage(image);
				setValue(newImageResized);
				onChange?.(newImageResized);
			} catch (e) {
				console.error('Cannot convert avatar img to base64 :' + e);
			} finally {
				setIsLoading(false);
			}
		}
	};

	useEffect(() => {
		if (avatarFile) {
			setValue(avatarFile);
		}
	}, [avatarFile]);

	return (
		<div className="flex xl:flex-col justify-center gap-[12px] xl:gap-0 xl:justify-normal items-center pb-[18px] xl:pb-0 xl:w-[140px] xl:h-[168px]">
			<div
				className={cls(
					'w-[100px] md:w-[150px] xl:w-[140px]',
					containerClassName
				)}
			>
				<input
					type="file"
					style={{ display: 'none' }}
					onChange={(e) => handleChangeAvatar(e)}
					accept={AVATAR_ACCEPT}
					name="avatarFile"
					ref={inputUploadAvatar}
					onClick={(e: any) => {
						e.target.value = null;
					}}
				/>
				{filePath ? (
					<img
						src={filePath}
						className="object-cover w-[100px] md:w-[150px] xl:w-[140px] h-[133px] md:h-[200px] xl:h-[168px]"
					/>
				) : (
					<img
						src={Images.iconAvatar}
						className="w-[100px] md:w-[150px] xl:w-[140px] h-[133px] md:h-[200px] xl:h-[168px]"
					/>
				)}
			</div>
			<div className="text-center w-[75px] md:w-[80px] xl:w-auto">
				<Button
					icon={
						!isMobile && (
							<img className="w-[14px] h-[14px]" src={Images.iconUploadImage} />
						)
					}
					className={cls(
						'w-20 xl:w-[140px] h-7 sm:h-8 xl:h-7 mt-2 text-[13px] rounded xl:rounded-none',
						isMobile && 'bg-white border-cornflower text-cornflower'
					)}
					loading={isLoading}
					onClick={onUploadAvatar}
					type={isMobile ? 'default' : 'primary'}
				>
					{isMobile ? '업로드' : t('common:photo-upload')}
				</Button>
				{!hiddenButtonDownload && (
					<Button
						icon={
							!isMobile && (
								<img
									className="w-[14px] h-[14px]"
									src={Images.iconDownloadAvatar}
								/>
							)
						}
						loading={isLoading}
						className={cls(
							'w-20 xl:w-[140px] h-7 sm:h-8 xl:h-7 mt-[5px] text-[13px] rounded xl:rounded-none',
							isMobile && 'bg-cornflower border-cornflower text-white'
						)}
						onClick={onDownloadAvatar}
						type="default"
					>
						{isMobile ? '다운로드' : t('common:photo-download')}
					</Button>
				)}

				{(filePath || value) && (
					<p
						className="cursor-pointer mt-[8px] xl:mt-[15px] font-medium text-[12px] text-cornflower xl:text-font8 underline"
						onClick={onRemoveAvatar}
					>
						{t('common:erase-the-photo')}
					</p>
				)}
			</div>
		</div>
	);
};

export default Avatar;
